/**
 * GPUMemoryTracker — wraps `webgpu-memory` for GPU resource tracking.
 *
 * Graceful no-op if the optional dependency is not installed.
 */

import { Logger } from '../core/Logger.js';

export interface GPUMemorySnapshot {
  textures: number;
  buffers: number;
  totalBytes: number;
}

export class GPUMemoryTracker {
  private getMemoryUsage: (() => GPUMemorySnapshot) | null = null;

  async init(device?: GPUDevice): Promise<void> {
    if (!device) {
      Logger.debug('GPUMemory', 'No GPUDevice provided — tracking disabled');
      return;
    }

    try {
      // Dynamically import webgpu-memory (optional dependency)
      // @ts-expect-error — webgpu-memory is an optional dependency
      const mod = await import('webgpu-memory').catch(() => null) as any;
      if (!mod) {
        Logger.debug('GPUMemory', 'webgpu-memory not installed — tracking disabled');
        return;
      }

      this.getMemoryUsage = () => {
        const info = mod.getWebGPUMemoryUsage(device);
        return {
          textures: info.textures?.count ?? 0,
          buffers: info.buffers?.count ?? 0,
          totalBytes: info.total ?? 0,
        };
      };

      Logger.info('GPUMemory', 'GPU memory tracking enabled');
    } catch {
      Logger.debug('GPUMemory', 'Failed to initialize webgpu-memory');
    }
  }

  /**
   * Take a snapshot of current GPU memory usage.
   */
  snapshot(): GPUMemorySnapshot {
    if (!this.getMemoryUsage) {
      return { textures: 0, buffers: 0, totalBytes: 0 };
    }
    try {
      return this.getMemoryUsage();
    } catch {
      return { textures: 0, buffers: 0, totalBytes: 0 };
    }
  }

  /**
   * Log the delta between two snapshots.
   */
  logDelta(prev: GPUMemorySnapshot, curr: GPUMemorySnapshot): void {
    const dTextures = curr.textures - prev.textures;
    const dBuffers = curr.buffers - prev.buffers;
    const dBytes = curr.totalBytes - prev.totalBytes;

    const sign = (n: number) => (n >= 0 ? '+' : '') + n;
    Logger.info(
      'GPUMemory',
      `Delta: textures ${sign(dTextures)}, buffers ${sign(dBuffers)}, bytes ${sign(dBytes)} (${(curr.totalBytes / 1024 / 1024).toFixed(1)} MB total)`,
    );
  }

  get available(): boolean {
    return this.getMemoryUsage !== null;
  }
}
